import 'projects/sharedComponents/src/breedSummary/legacyBreedSummaryCard.scss';

import { Button, Card, CardProps, Grid, Stack, Typography } from '@mui/material';
import {
  CareResourcesReasonCategory,
  LegacyBreedSummaryCardFragment,
} from 'projects/sharedComponents/src/generated/graphql-client-types';
import { FC, VFC } from 'react';
import { Heap, HeapAction } from 'clientServerShared/types/eventTracking';
import { ProductTypePill, productTypeToLabelMap } from 'projects/sharedComponents/src/pet/productType/productTypePill';

import { AccountDetails } from 'clientServerShared/types/account';
import { CareResourcesResultsBanner } from 'projects/sharedComponents/src/pet/careResources/resultsBanner/careResourcesResultsBanner';
import { CircularIconContainer } from 'projects/sharedComponents/src/icons/layoutComponents/circularIconContainer';
import { ClickableCard } from 'projects/sharedComponents/src/cards/clickableCard';
import { LegacyBreedHalo } from 'projects/sharedComponents/src/dogAvatar/legacy/legacyBreedHalo';
import { LegacyBreedSummaryBars } from 'projects/sharedComponents/src/breedSummary/legacyBreedSummaryBars';
import { PawShieldFilledIcon } from 'projects/sharedComponents/src/icons/pawShieldIcon';
import { RenderIf } from 'projects/sharedComponents/src/renderStates/renderIf';
import { ShareAccordion } from 'projects/sharedComponents/src/pet/shareUI/accordion/shareAccordion';
import { buildMyEvLegacyLink } from 'projects/sharedComponents/src/services/links/linkBuilder';
import { hisOrHer } from 'projects/sharedComponents/src/services/formatting/sexFormatting';
import { shorten } from 'projects/sharedComponents/src/services/stringFormatters/stringFormatters';
import { styled } from '@mui/material/styles';

type LegacyBreedSummaryCardProps = {
  dog: LegacyBreedSummaryCardFragment;
  showProductType?: boolean;
  showShare?: boolean;
  shouldShowViewButtons?: boolean;
  showCareResourcesBanner?: boolean;
  allowSupermuttCallout?: boolean;
  accountDetails: AccountDetails;
} & Heap;

export const LegacyBreedSummaryCard: FC<LegacyBreedSummaryCardProps> = ({
  dog,
  showProductType = true,
  showShare = false,
  shouldShowViewButtons = false,
  accountDetails,
  showCareResourcesBanner = false,
  allowSupermuttCallout = false,
  heapMetadata,
}) => {
  const dogName = shorten(dog.name, 25);

  const isBreeder = accountDetails?.user?.permissions.isBreeder;
  if (isBreeder) {
    showProductType = false;
  }

  const isVet = accountDetails?.user?.permissions.isVet;
  let viewBreedButtonText = 'View Breed & Ancestry';
  if (isVet) {
    viewBreedButtonText = 'View Breed';
  }

  const countryCode = accountDetails.user?.countryCode ?? 'US';

  const { breedPct: supermuttPercentage } = dog.genotype?.breedMix.find(({ breedName }) =>
    breedName.match(/supermutt/i)
  ) ?? { breedPct: 0 };

  return (
    <SummaryCard>
      <Stack spacing={3}>
        <Grid container spacing={3} className="breedSummaryCard-clickableContent">
          <Grid item container className="breedSummaryCard-haloContainer" sm={3} direction="column" alignItems="center">
            <Grid item>
              <LegacyBreedHalo dog={dog} width={138} />
            </Grid>
            <Grid item>
              {!!dog.swabSummary.productType && showProductType && (
                <ProductTypePill productLabel={productTypeToLabelMap[dog.swabSummary.productType]} />
              )}
            </Grid>
          </Grid>
          <Grid className="breedSummaryCard-dogDetails" container item direction="column" sm={9} spacing={1}>
            <Grid item container>
              <Grid item sm={6}>
                <Typography variant="h5" component="p">
                  <strong>{dogName}</strong>
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              <Typography>
                <strong>{dog.genotype?.breedType}</strong>
              </Typography>
            </Grid>
            <Grid item className="breedSummaryCard-breedBars-container">
              <LegacyBreedSummaryBars dog={dog} />
            </Grid>
          </Grid>
          <RenderIf condition={shouldShowViewButtons}>
            <Grid item xs={12} sm={12} className="breedSummaryCard-viewButtons">
              <Button
                className="breedSummaryCard-viewBreedResults"
                variant="contained"
                color="primary"
                href={buildMyEvLegacyLink(`/members/results/breed?i=${dog.petNum}&countryCode=${countryCode}`)}
              >
                {viewBreedButtonText}
              </Button>
              <Button
                className="breedSummaryCard-viewProfile"
                variant="outlined"
                color="secondary"
                href={buildMyEvLegacyLink(`/members/results/profile?i=${dog.petNum}`)}
              >
                View Profile
              </Button>
            </Grid>
          </RenderIf>
        </Grid>
        <Stack spacing={2}>
          <RenderIf condition={allowSupermuttCallout && !isBreeder}>
            <SupermuttCallout
              petNum={dog.petNum}
              petName={dog.name}
              petSex={dog.sex}
              supermuttPercentage={supermuttPercentage}
              heapMetadata={heapMetadata}
            />
          </RenderIf>
          <RenderIf condition={showCareResourcesBanner}>
            <CareResourcesResultsBanner pet={dog} category={CareResourcesReasonCategory.Breed} />
          </RenderIf>
          <RenderIf condition={showShare}>
            <ShareAccordion shareOptions={dog.shareOptions} heapCategory={heapMetadata?.heapCategory} />
          </RenderIf>
        </Stack>
      </Stack>
    </SummaryCard>
  );
};

type SupermuttCalloutProps = Heap & {
  supermuttPercentage?: number;
  petNum: LegacyBreedSummaryCardProps['dog']['petNum'];
  petName: LegacyBreedSummaryCardProps['dog']['name'];
  petSex: LegacyBreedSummaryCardProps['dog']['sex'];
};
const SupermuttCallout: VFC<SupermuttCalloutProps> = ({
  petNum,
  petName,
  petSex,
  heapMetadata,
  supermuttPercentage = 0,
}) => {
  const qualifierText: 'exceptional' | 'special' = Math.round(supermuttPercentage) > 25 ? 'exceptional' : 'special';
  const hisOrHerText = hisOrHer(petSex);
  return (
    <RenderIf condition={!!supermuttPercentage}>
      <ClickableCard
        heapMetadata={{ ...heapMetadata, heapAction: HeapAction.NAVIGATE }}
        slotProps={{
          cardActionArea: {
            href: `/members/results/breed-and-ancestry/mix?i=${petNum}`,
          },
        }}
        icon={
          <CircularIconContainer
            shapeContainerProps={{
              alignItems: 'center',
              sx: { height: 56, width: 56, flexShrink: 0 },
            }}
          >
            <PawShieldFilledIcon sx={{ fontSize: 32 }} />
          </CircularIconContainer>
        }
        title={`${petName} is one ${qualifierText} Supermutt!`}
        subheader={`Explore ${hisOrHerText} unique Supermutt heritage, which comes from many generations of mixed-breed dogs.`}
      />
    </RenderIf>
  );
};

const SummaryCard = styled(Card)<CardProps>(({ theme }) => ({
  borderRadius: 0,
  boxShadow: `0 1px 8px ${theme.palette.helix.greyLight}`,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
}));
